import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthFetch } from '../hooks/useAuthFetch';

const C = {
  bg: '#12141f', card: '#1a1d2e', border: '#2a2d3e',
  text: '#e2e8f0', muted: '#94a3b8', faint: '#64748b',
  accent: '#41B073', green: '#22c55e', red: '#ef4444',
  orange: '#f97316', yellow: '#eab308',
};
const inputStyle = { padding: '8px 12px', borderRadius: 8, background: C.card, border: `1px solid ${C.border}`, color: C.text, fontSize: 13, fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' };

// ─── Définitions des entités importables ─────────────────────────────────────

const ENTITIES = {
  clients: {
    label: 'Clients',
    icon: '👥',
    endpoint: '/api/import/clients',
    required: ['nom'],
    fields: [
      { key: 'nom',         label: 'Nom *',          aliases: ['nom', 'name', 'société', 'societe', 'entreprise', 'company', 'raison sociale'] },
      { key: 'type',        label: 'Type',           aliases: ['type', 'categorie', 'catégorie'] },
      { key: 'statut',      label: 'Statut',         aliases: ['statut', 'status', 'état', 'etat'] },
      { key: 'secteur',     label: 'Secteur',        aliases: ['secteur', 'sector', 'activité', 'activite', 'domaine'] },
      { key: 'adresse',     label: 'Adresse',        aliases: ['adresse', 'address', 'rue'] },
      { key: 'ville',       label: 'Ville',          aliases: ['ville', 'city', 'localité', 'localite'] },
      { key: 'code_postal', label: 'Code postal',    aliases: ['code_postal', 'cp', 'zip', 'code postal', 'postal'] },
      { key: 'pays',        label: 'Pays',           aliases: ['pays', 'country'] },
      { key: 'telephone',   label: 'Téléphone',      aliases: ['telephone', 'téléphone', 'tel', 'phone', 'tél'] },
      { key: 'email',       label: 'Email',          aliases: ['email', 'mail', 'e-mail', 'courriel'] },
      { key: 'site_web',    label: 'Site web',       aliases: ['site_web', 'site', 'web', 'url', 'website'] },
      { key: 'notes',       label: 'Notes',          aliases: ['notes', 'note', 'commentaire', 'commentaires', 'remarque'] },
    ],
    template: 'nom,type,statut,secteur,adresse,ville,code_postal,telephone,email,site_web,notes\nDupont & Associés,PME,Prospect,Conseil,12 rue de la Paix,Paris,75001,01 23 45 67 89,contact@dupont.fr,https://dupont.fr,Premier contact salon\nBoulangerie Martin,TPE,Client actif,Alimentaire,5 place du Marché,Amiens,80000,,,,'
  },
  contacts: {
    label: 'Contacts',
    icon: '🤝',
    endpoint: '/api/import/contacts',
    required: ['nom', 'client_nom'],
    fields: [
      { key: 'client_nom',  label: 'Client (nom exact) *', aliases: ['client_nom', 'client', 'entreprise', 'société', 'societe', 'company'] },
      { key: 'nom',         label: 'Nom *',                aliases: ['nom', 'name', 'last_name', 'lastname'] },
      { key: 'prenom',      label: 'Prénom',               aliases: ['prenom', 'prénom', 'first_name', 'firstname', 'prénom'] },
      { key: 'poste',       label: 'Poste / Fonction',     aliases: ['poste', 'fonction', 'title', 'job', 'rôle', 'role'] },
      { key: 'email',       label: 'Email',                aliases: ['email', 'mail', 'e-mail', 'courriel'] },
      { key: 'telephone',   label: 'Téléphone',            aliases: ['telephone', 'téléphone', 'tel', 'phone', 'tél'] },
      { key: 'mobile',      label: 'Mobile',               aliases: ['mobile', 'portable', 'gsm', 'cell'] },
      { key: 'notes',       label: 'Notes',                aliases: ['notes', 'note', 'commentaire'] },
    ],
    template: 'client_nom,nom,prenom,poste,email,telephone,mobile\nDupont & Associés,Dupont,Jean,Directeur général,j.dupont@dupont.fr,01 23 45 67 89,06 12 34 56 78\nBoulangerie Martin,Martin,Sophie,Gérante,s.martin@boulangerie.fr,,'
  },
};

// ─── Parseur CSV — séparateur auto-détecté (virgule ou point-virgule) ─────────
function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };

  // Auto-détection du séparateur sur la première ligne
  const sep = (lines[0].split(';').length > lines[0].split(',').length) ? ';' : ',';

  const parseRow = (line) => {
    const cols = [];
    let cur = '', inQuote = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuote && line[i + 1] === '"') { cur += '"'; i++; }
        else inQuote = !inQuote;
      } else if (ch === sep && !inQuote) {
        cols.push(cur.trim()); cur = '';
      } else {
        cur += ch;
      }
    }
    cols.push(cur.trim());
    return cols;
  };

  const headers = parseRow(lines[0]).map(h => h.toLowerCase().trim());
  const rows    = lines.slice(1).map(l => {
    const vals = parseRow(l);
    const obj  = {};
    headers.forEach((h, i) => { obj[h] = vals[i] ?? ''; });
    return obj;
  }).filter(r => Object.values(r).some(v => v !== ''));

  return { headers, rows };
}

// ─── Auto-mapping colonnes CSV → champs entité ────────────────────────────────
function autoMap(headers, fields) {
  const mapping = {};
  fields.forEach(f => {
    const found = headers.find(h => f.aliases.some(a => h === a || h.replace(/[\s_-]/g, '') === a.replace(/[\s_-]/g, '')));
    mapping[f.key] = found || '';
  });
  return mapping;
}

// ─── Téléchargement template CSV ─────────────────────────────────────────────
function downloadTemplate(entityKey) {
  const e    = ENTITIES[entityKey];
  const blob = new Blob([e.template], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a'); a.href = url; a.download = `template_${entityKey}.csv`;
  a.click(); URL.revokeObjectURL(url);
}

// ─── Composant principal ──────────────────────────────────────────────────────
export default function ImportPage() {
  const fetchWithAuth = useAuthFetch();
  const navigate      = useNavigate();
  const fileRef       = useRef(null);

  const [entityKey, setEntityKey]   = useState('clients');
  const [step, setStep]             = useState(1); // 1=upload 2=mapping 3=preview 4=result
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows]       = useState([]);
  const [mapping, setMapping]       = useState({});
  const [fileName, setFileName]     = useState('');
  const [importing, setImporting]   = useState(false);
  const [result, setResult]         = useState(null);
  const [dragOver, setDragOver]     = useState(false);

  const entity = ENTITIES[entityKey];

  const reset = () => {
    setCsvHeaders([]); setCsvRows([]); setMapping({}); setFileName('');
    setStep(1); setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleEntityChange = (key) => { setEntityKey(key); reset(); };

  const processFile = useCallback((file) => {
    if (!file || !file.name.match(/\.(csv|txt)$/i)) {
      alert('Format accepté : fichier .csv ou .txt'); return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const { headers, rows } = parseCSV(e.target.result);
      if (headers.length === 0) { alert('Fichier vide ou non reconnu'); return; }
      setCsvHeaders(headers);
      setCsvRows(rows);
      setMapping(autoMap(headers, ENTITIES[entityKey].fields));
      setStep(2);
    };
    reader.readAsText(file, 'UTF-8');
  }, [entityKey]);

  const handleFileChange = (e) => processFile(e.target.files[0]);
  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); processFile(e.dataTransfer.files[0]); };

  // Applique le mapping pour construire les rows finaux
  const buildRows = () => csvRows.map(raw => {
    const obj = {};
    entity.fields.forEach(f => {
      const col = mapping[f.key];
      obj[f.key] = col ? (raw[col] || '') : '';
    });
    return obj;
  });

  const handleImport = async () => {
    setImporting(true);
    try {
      const rows = buildRows();
      const res  = await fetchWithAuth(entity.endpoint, { method: 'POST', body: JSON.stringify({ rows }) });
      const data = await res.json();
      setResult(data);
      setStep(4);
    } finally { setImporting(false); }
  };

  const previewRows = buildRows().slice(0, 8);

  // ── Rendu ──────────────────────────────────────────────────────────────────

  const StepDot = ({ n, label }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, background: step >= n ? C.accent : C.border, color: step >= n ? '#fff' : C.faint, transition: 'background 0.2s' }}>{n}</div>
      <span style={{ fontSize: 12, color: step >= n ? C.muted : C.faint, whiteSpace: 'nowrap' }}>{label}</span>
    </div>
  );

  return (
    <div style={{ padding: 28, maxWidth: 860, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text }}>Import de données</h1>
        <div style={{ fontSize: 13, color: C.faint, marginTop: 4 }}>Importez vos clients ou contacts depuis un fichier CSV</div>
      </div>

      {/* Stepper */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32, flexWrap: 'wrap' }}>
        <StepDot n={1} label="Fichier" />
        <div style={{ flex: 1, height: 1, background: C.border, minWidth: 20 }} />
        <StepDot n={2} label="Colonnes" />
        <div style={{ flex: 1, height: 1, background: C.border, minWidth: 20 }} />
        <StepDot n={3} label="Prévisualisation" />
        <div style={{ flex: 1, height: 1, background: C.border, minWidth: 20 }} />
        <StepDot n={4} label="Résultats" />
      </div>

      {/* ── Étape 1 : sélection entité + upload ── */}
      {step === 1 && (
        <div>
          {/* Choix de l'entité */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
            {Object.entries(ENTITIES).map(([key, e]) => (
              <button key={key} onClick={() => handleEntityChange(key)}
                style={{ flex: 1, padding: '14px 16px', borderRadius: 10, border: `2px solid ${entityKey === key ? C.accent : C.border}`, background: entityKey === key ? C.accent + '15' : C.card, color: entityKey === key ? C.accent : C.muted, cursor: 'pointer', fontFamily: 'inherit', fontWeight: entityKey === key ? 700 : 400, fontSize: 14, transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 22 }}>{e.icon}</span>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontWeight: 600 }}>{e.label}</div>
                  <div style={{ fontSize: 11, color: C.faint, fontWeight: 400 }}>{e.fields.filter(f => f.key !== 'notes').length} champs disponibles</div>
                </div>
              </button>
            ))}
          </div>

          {/* Zone de dépôt */}
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
            style={{ border: `2px dashed ${dragOver ? C.accent : C.border}`, borderRadius: 12, padding: '48px 24px', textAlign: 'center', cursor: 'pointer', background: dragOver ? C.accent + '08' : C.bg, transition: 'all 0.15s' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📂</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.muted, marginBottom: 6 }}>Glissez votre fichier CSV ici</div>
            <div style={{ fontSize: 13, color: C.faint, marginBottom: 16 }}>ou cliquez pour parcourir</div>
            <div style={{ display: 'inline-block', padding: '7px 18px', borderRadius: 8, background: C.accent, color: '#fff', fontSize: 13, fontWeight: 600 }}>Choisir un fichier</div>
            <div style={{ fontSize: 11, color: C.faint, marginTop: 14 }}>Formats acceptés : .csv · UTF-8 · Séparateur , ou ;</div>
            <input ref={fileRef} type="file" accept=".csv,.txt" onChange={handleFileChange} style={{ display: 'none' }} />
          </div>

          {/* Template download */}
          <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 12, color: C.faint }}>Pas encore de fichier ?</span>
            <button onClick={() => downloadTemplate(entityKey)} style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, border: `1px solid ${C.border}`, background: 'none', color: C.muted, cursor: 'pointer', fontFamily: 'inherit' }}>
              📥 Télécharger le template {entity.label}
            </button>
          </div>
        </div>
      )}

      {/* ── Étape 2 : mapping des colonnes ── */}
      {step === 2 && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Correspondance des colonnes</div>
              <div style={{ fontSize: 12, color: C.faint, marginTop: 2 }}>{fileName} · {csvRows.length} ligne{csvRows.length > 1 ? 's' : ''} détectée{csvRows.length > 1 ? 's' : ''}</div>
            </div>
            <button onClick={reset} style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, border: `1px solid ${C.border}`, background: 'none', color: C.muted, cursor: 'pointer', fontFamily: 'inherit' }}>← Changer de fichier</button>
          </div>

          <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {['Champ attendu', 'Colonne CSV détectée', 'Aperçu (1ère ligne)'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: C.faint, fontWeight: 600, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entity.fields.map(f => {
                  const isRequired = entity.required.includes(f.key);
                  const mapped     = mapping[f.key];
                  const preview    = mapped && csvRows[0] ? csvRows[0][mapped] || '' : '';
                  const missing    = isRequired && !mapped;
                  return (
                    <tr key={f.key} style={{ borderBottom: `1px solid ${C.border}22`, background: missing ? C.red + '08' : 'transparent' }}>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ fontSize: 13, color: missing ? C.red : C.text, fontWeight: isRequired ? 600 : 400 }}>
                          {f.label}
                        </span>
                        {missing && <span style={{ marginLeft: 8, fontSize: 11, color: C.red }}>⚠ Requis</span>}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <select value={mapping[f.key] || ''} onChange={e => setMapping(m => ({ ...m, [f.key]: e.target.value }))}
                          style={{ ...inputStyle, width: 'auto', minWidth: 180, padding: '6px 10px', cursor: 'pointer', borderColor: missing ? C.red : C.border }}>
                          <option value="">— Ne pas importer —</option>
                          {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '10px 14px', fontSize: 12, color: preview ? C.muted : C.faint, fontStyle: preview ? 'normal' : 'italic' }}>
                        {preview || '(vide)'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setStep(3)}
              disabled={entity.required.some(k => !mapping[k])}
              style={{ padding: '10px 24px', borderRadius: 8, background: entity.required.some(k => !mapping[k]) ? C.border : C.accent, border: 'none', color: entity.required.some(k => !mapping[k]) ? C.faint : '#fff', cursor: entity.required.some(k => !mapping[k]) ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit' }}>
              Prévisualiser →
            </button>
            <button onClick={reset} style={{ padding: '10px 18px', borderRadius: 8, background: 'none', border: `1px solid ${C.border}`, color: C.muted, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>Annuler</button>
          </div>
        </div>
      )}

      {/* ── Étape 3 : prévisualisation ── */}
      {step === 3 && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Prévisualisation de l'import</div>
              <div style={{ fontSize: 12, color: C.faint, marginTop: 2 }}>{csvRows.length} {entity.label.toLowerCase()} à importer — aperçu des 8 premières lignes</div>
            </div>
          </div>

          <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                    {entity.fields.filter(f => mapping[f.key]).map(f => (
                      <th key={f.key} style={{ padding: '9px 12px', textAlign: 'left', fontSize: 11, color: C.faint, fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{f.label.replace(' *', '')}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${C.border}22`, background: i % 2 === 0 ? 'transparent' : '#ffffff05' }}>
                      {entity.fields.filter(f => mapping[f.key]).map(f => (
                        <td key={f.key} style={{ padding: '9px 12px', fontSize: 12, color: row[f.key] ? C.text : C.faint, fontStyle: row[f.key] ? 'normal' : 'italic', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row[f.key] || '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {csvRows.length > 8 && (
              <div style={{ padding: '10px 14px', borderTop: `1px solid ${C.border}`, fontSize: 12, color: C.faint }}>
                … et {csvRows.length - 8} ligne{csvRows.length - 8 > 1 ? 's' : ''} supplémentaire{csvRows.length - 8 > 1 ? 's' : ''}
              </div>
            )}
          </div>

          {/* Récapitulatif */}
          <div style={{ background: C.card, border: `1px solid ${C.accent}33`, borderRadius: 10, padding: '14px 18px', marginBottom: 20, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div><span style={{ fontSize: 20, fontWeight: 800, color: C.accent, fontFamily: 'DM Mono, monospace' }}>{csvRows.length}</span><div style={{ fontSize: 11, color: C.faint }}>lignes à traiter</div></div>
            <div><span style={{ fontSize: 20, fontWeight: 800, color: C.green, fontFamily: 'DM Mono, monospace' }}>{entity.fields.filter(f => mapping[f.key]).length}</span><div style={{ fontSize: 11, color: C.faint }}>colonnes mappées</div></div>
            <div><span style={{ fontSize: 20, fontWeight: 800, color: C.muted, fontFamily: 'DM Mono, monospace' }}>{entity.fields.filter(f => !mapping[f.key]).length}</span><div style={{ fontSize: 11, color: C.faint }}>colonnes ignorées</div></div>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleImport} disabled={importing}
              style={{ padding: '10px 28px', borderRadius: 8, background: C.green, border: 'none', color: '#fff', cursor: importing ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 14, fontFamily: 'inherit' }}>
              {importing ? 'Import en cours…' : `⬆ Importer ${csvRows.length} ${entity.label.toLowerCase()}`}
            </button>
            <button onClick={() => setStep(2)} style={{ padding: '10px 18px', borderRadius: 8, background: 'none', border: `1px solid ${C.border}`, color: C.muted, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>← Retour</button>
          </div>
        </div>
      )}

      {/* ── Étape 4 : résultats ── */}
      {step === 4 && result && (
        <div>
          {/* Résumé */}
          <div className="kpi-grid" style={{ marginBottom: 24 }}>
            <div style={{ background: C.card, border: `1px solid ${C.green}44`, borderRadius: 10, padding: '18px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 36, fontWeight: 800, color: C.green, fontFamily: 'DM Mono, monospace' }}>{result.inserted}</div>
              <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>✅ Importé{result.inserted > 1 ? 's' : ''}</div>
            </div>
            <div style={{ background: C.card, border: `1px solid ${result.skipped > 0 ? C.orange + '44' : C.border}`, borderRadius: 10, padding: '18px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 36, fontWeight: 800, color: result.skipped > 0 ? C.orange : C.faint, fontFamily: 'DM Mono, monospace' }}>{result.skipped}</div>
              <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>⚠ Ignoré{result.skipped > 1 ? 's' : ''}</div>
            </div>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '18px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 36, fontWeight: 800, color: C.muted, fontFamily: 'DM Mono, monospace' }}>{result.total}</div>
              <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>📄 Total traité{result.total > 1 ? 's' : ''}</div>
            </div>
          </div>

          {/* Message succès */}
          {result.inserted > 0 && (
            <div style={{ background: C.green + '15', border: `1px solid ${C.green}44`, borderRadius: 10, padding: '14px 18px', marginBottom: 16, fontSize: 13, color: C.green, fontWeight: 600 }}>
              ✅ {result.inserted} {entity.label.toLowerCase()} importé{result.inserted > 1 ? 's' : ''} avec succès dans la base de données.
            </div>
          )}

          {/* Erreurs détaillées */}
          {result.errors?.length > 0 && (
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
              <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`, fontSize: 13, fontWeight: 600, color: C.orange }}>
                ⚠ {result.errors.length} ligne{result.errors.length > 1 ? 's' : ''} ignorée{result.errors.length > 1 ? 's' : ''}
              </div>
              <div className="modal-scroll" style={{ maxHeight: 280, overflowY: 'auto' }}>
                {result.errors.map((e, i) => (
                  <div key={i} style={{ padding: '10px 16px', borderBottom: `1px solid ${C.border}22`, display: 'flex', gap: 12, fontSize: 12 }}>
                    <span style={{ color: C.faint, fontFamily: 'DM Mono, monospace', minWidth: 50 }}>Ligne {e.row}</span>
                    <span style={{ color: C.muted }}>{e.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={reset} style={{ padding: '10px 24px', borderRadius: 8, background: C.accent, border: 'none', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit' }}>
              + Nouvel import
            </button>
            <button onClick={() => navigate(`/${entityKey}`)} style={{ padding: '10px 20px', borderRadius: 8, background: 'none', border: `1px solid ${C.border}`, color: C.muted, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>
              {entity.icon} Voir les {entity.label.toLowerCase()}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
